import { AudioOutlined, DeleteOutlined, FormOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { Modal, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Contact } from 'api/ContactsApi';
import { Message, MessageStatusEnum, MessageTypeEnum } from 'api/MessagesApi';
import { useGetAllMessagesSysAdminQuery, useRemoveSysAdminMessageMutation } from 'api/SysAdminApi';
import imagePreview from 'assets/preview.svg';
import { shouldSkipSysAdminPatientContentQuery } from 'components/SysAdmin/sysAdminIdentityGuards';
import { SYSADMIN_PREVIEW_MESSAGE_MODAL_NAME } from 'components/SysAdmin/SysAdminPreviewMessageModalComponent/SysAdminPreviewMessageModalComponent';
import { useState } from 'react';
import { openModal } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { displayDeliveryDate } from 'utils/utils';
import './SysAdminMessageListComponent.css';

type AllProps = {
  userId?: string;
  patientProfileId?: string;
};

const PAGE_SIZE = 10;

function SysAdminMessageListComponent({ userId, patientProfileId }: AllProps) {
  const dispatch = useAppDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [removeSysAdminMessage, { isLoading: isRemovingMessage }] = useRemoveSysAdminMessageMutation();
  const [messageApi, contextHolder] = message.useMessage();
  const messageQueryParams = {
    ...(userId ? { userId } : {}),
    ...(patientProfileId ? { patientProfileId } : {}),
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE
  };

  const { data: sysAdminMessageListData, isFetching } = useGetAllMessagesSysAdminQuery(messageQueryParams,
  { skip: shouldSkipSysAdminPatientContentQuery(userId, patientProfileId) });

  const onTableChanged = (pagination, _filters, _sorter) => {
    setCurrentPage(pagination.current || 1);
  };

  const previewMessage = (id: string) => {
    dispatch(openModal({ name: SYSADMIN_PREVIEW_MESSAGE_MODAL_NAME, data: { id } }));
  };

  const renderPreviewIcon = (message: Message) => {
    const hasPreview = message.type === MessageTypeEnum.Text || message.fileName !== '';

    return (
      hasPreview && (
        <Tooltip title="Preview">
          <img src={imagePreview} alt="preview" onClick={() => previewMessage(message.id)} className="cursor-pointer" />
        </Tooltip>
      )
    );
  };

  const renderTypeIcon = (type: MessageTypeEnum) => {
    if (type === MessageTypeEnum.Video) {
      return <VideoCameraOutlined />;
    } else if (type === MessageTypeEnum.Audio) {
      return <AudioOutlined />;
    } else {
      return <FormOutlined />;
    }
  };

  const renderContacts = (contacts: Contact[]) => {
    return (
      contacts &&
      contacts.map((contact) => (
        <div key={contact.id}>
          <Typography.Text>
            {contact.name} ({contact.email})
          </Typography.Text>
        </div>
      ))
    );
  };

  const renderStatus = (status: MessageStatusEnum) => {
    const statuses = {
      [MessageStatusEnum.Published]: <Tag color="success">Completed</Tag>,
      [MessageStatusEnum.Delivering]: <Tag color="geekblue">Delivering</Tag>,
      [MessageStatusEnum.Delivered]: <Tag color="geekblue">Delivered</Tag>
    };

    return statuses[status] ? statuses[status] : <Tag color="default">Draft</Tag>;
  };

  const removeMessage = async (messageRow: Message) => {
    Modal.confirm({
      title: 'Delete message',
      content: 'Are you sure you want to delete this message? This action may remove patient-created content.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await removeSysAdminMessage({ messageId: messageRow.id }).unwrap();
          messageApi.success('Message deleted successfully.');
        } catch (e) {
          messageApi.error('Failed to delete message. Please try again.');
        }
      }
    });
  };

  const columns: ColumnsType<Message> = [
    {
      title: 'Message Name',
      key: 'name',
      render: (_, message) => (
        <div className="message-title">
          <Typography.Text>
            {message.name} {renderPreviewIcon(message)}
          </Typography.Text>
        </div>
      )
    },
    {
      title: 'Type',
      key: 'type',
      dataIndex: 'type',
      render: (type) => renderTypeIcon(type)
    },
    {
      title: 'Recipient(s)',
      key: 'recipient',
      dataIndex: 'contacts',
      render: (contacts: Contact[]) => renderContacts(contacts)
    },
    {
      title: 'Delivery Date',
      key: 'delivery',
      render: (_, message) => displayDeliveryDate(message)
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      render: (status: MessageStatusEnum) => renderStatus(status)
    },
    {
      render: (_, messageRow) => (
        <Tooltip title="Delete Message">
          <button
            type="button"
            aria-label="Delete message"
            onClick={() => removeMessage(messageRow)}
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <DeleteOutlined />
          </button>
        </Tooltip>
      )
    }
  ];

  return (
    <>
      {contextHolder}
      <div className="sys-admin-user-list-component">
        <h3>Messages ({sysAdminMessageListData?.total || 0})</h3>

        <Table
          columns={columns}
          dataSource={sysAdminMessageListData?.list || []}
          pagination={{
            defaultPageSize: PAGE_SIZE,
            current: currentPage,
            total: sysAdminMessageListData?.total || 0
          }}
          onChange={onTableChanged}
          rowKey="id"
          bordered
          loading={isFetching || isRemovingMessage}
        />
      </div>
    </>
  );
}

export default SysAdminMessageListComponent;
